// src/stores/useSubscriptionStatusStore.js
import { defineStore } from "pinia";
import api from "@/services/api"; // ajuste para o caminho do seu axios instance

const POLL_INTERVAL_MS = 1 * 60 * 1000; // 1 minutos

export const useSubscriptionStatusStore = defineStore("subscriptionStatus", {
  state: () => ({
    status: null, // 'Active' | 'Inactive' | null
    paymentStatus: null, // 'Approved' | 'Pending' | etc
    isRecurring: false,
    planName: null,

    // ✅ MUDANÇA: substituímos o flag único `wasRecentlyDowngraded` por dois
    // flags semanticamente distintos, alinhados com o DTO do backend:
    //
    //   wasNaturalExpiration : período pago da assinatura acabou (pausa que
    //                          venceu, avulsa que venceu). Sub ficou Expired.
    //                          Dispara SubscriptionExpiredModal.
    //
    //   wasPaymentRejected   : MP recusou a renovação automática. Sub ficou
    //                          Cancelled + PaymentStatus Rejected/Cancelled.
    //                          Dispara RecurringPaymentRejectedModal.
    //
    // Decisão de produto: mutuamente exclusivos — no máximo um é true por
    // resposta do polling. O backend prioriza expiração natural se ambos
    // os cenários forem detectados no mesmo poll (caso-limite).
    wasNaturalExpiration: false,
    wasPaymentRejected: false,

    previousPlanName: null,

    // ✅ NOVO: data em que a assinatura anterior encerrou-se. Vem do backend
    // em UTC (ISO 8601). Consumida pelo DashboardLayout para compor a frase
    // do modal de expiração ("Sua assinatura Plus expirou em 14/05/2026 e
    // você foi movido para o plano gratuito.").
    //
    // Mantemos como string (não Date) porque é só consumida para formatação
    // — o frontend faz `new Date(previousEndDate)` no momento de exibir.
    // Evitamos um Date no state para não criar diffs reativos espúrios em
    // polls subsequentes (Date com mesma timestamp não é === a outro Date).
    previousEndDate: null,

    _intervalId: null,
  }),

  getters: {
    isActive: (state) => state.status === "Active",
  },

  actions: {
    async fetchStatus() {
      try {
        const { useUserStore } = await import("@/stores/useUserStore");
        const userStore = useUserStore();

        const isManaged = userStore.currentUser?.loginContext === "managed";
        const endpoint = isManaged
          ? "/Subscriptions/manager-status"
          : "/Subscriptions/status";

        const response = await api.get(endpoint);
        this.status = response.data.status;
        this.paymentStatus = response.data.paymentStatus;
        this.isRecurring = response.data.isRecurring;
        this.planName = response.data.planName;

        // ✅ MUDANÇA: lê os dois flags novos do DTO. Note que o backend
        // continua enviando esses campos como `true` apenas no primeiro
        // poll após o evento (depois marca NotifiedAt no banco); polls
        // subsequentes virão com `false`, evitando re-exibição do modal.
        this.wasNaturalExpiration = response.data.wasNaturalExpiration ?? false;
        this.wasPaymentRejected = response.data.wasPaymentRejected ?? false;

        this.previousPlanName = response.data.previousPlanName;

        // ✅ NOVO: data de expiração da sub anterior (UTC, ISO string).
        // Pode ser null quando nenhum dos flags estiver true.
        this.previousEndDate = response.data.previousEndDate ?? null;
      } catch (error) {
        console.error(
          "[SubscriptionStatusStore] Erro ao consultar status:",
          error,
        );
      }
    },

    startPolling() {
      // Evita múltiplos intervalos
      if (this._intervalId) return;

      this.fetchStatus(); // consulta imediata

      this._intervalId = setInterval(() => {
        this.fetchStatus();
      }, POLL_INTERVAL_MS);
    },

    stopPolling() {
      if (this._intervalId) {
        clearInterval(this._intervalId);
        this._intervalId = null;
      }
      this.status = null;
      this.paymentStatus = null;
      this.isRecurring = false;

      // ✅ MUDANÇA: zera também os flags ao parar o polling (ex.: logout).
      // Sem isso, um próximo login do mesmo usuário (ou outro) poderia ver
      // valores stale e disparar modais indevidamente antes do primeiro
      // fetchStatus rodar.
      this.wasNaturalExpiration = false;
      this.wasPaymentRejected = false;
      this.previousPlanName = null;
      this.previousEndDate = null;
      this.planName = null;
    },
  },
});
